import { useEffect, useRef } from "react";
import { Sparkles, Maximize2 } from "lucide-react";
import L from "leaflet";
import { Badge } from "@/components/ui/badge";
import { PageSpinner } from "@/components/ui/spinner";
import { ROUTE_HEX, type DispatchTech, type TechRoute, type TechRouteStop, type ShopLocation } from "./dispatch-types";
import { addBasemapLayer } from "@/lib/map-tiles";

interface RouteRailProps {
    techs: DispatchTech[];
    routes: TechRoute[];
    shop: ShopLocation | null;
    loading: boolean;
    suggestionsByTech?: Map<string, { jobCount: number; label: string }>;
    onExpandTech?: (techId: string) => void;
}

/**
 * Bottom rail of the day view. Renders one small Leaflet map per technician,
 * showing the planned ordered stops for the day. Empty techs render a stub
 * card so the dispatcher can still see who has nothing routed yet.
 */
export function RouteRail({ techs, routes, shop, loading, suggestionsByTech, onExpandTech }: RouteRailProps) {
    useLeafletCss();

    if (loading) {
        return (
            <div className="rounded-lg border bg-card p-3">
                <RailHeader />
                <div className="flex h-32 items-center justify-center">
                    <PageSpinner />
                </div>
            </div>
        );
    }

    const techById = new Map(techs.map((t) => [t.id, t] as const));
    // PRE-CUTOVER #42: the route rail AND the standalone map both colour
    // each tech's polyline. To keep the two views in agreement we iterate
    // the SAME upstream array (`routes`, returned by /api/dispatch/tech-
    // routes in stable order) and key the colour off the tech id. The
    // map page mirrors this exact computation (`stableRouteColor` below)
    // so a dispatcher who jumps from the rail to the full map sees the
    // identical polyline colour.
    const visibleRoutes = routes.filter((r) => r.jobs.length > 0);

    return (
        <div className="rounded-lg border bg-card p-3">
            <RailHeader />
            {visibleRoutes.length === 0 ? (
                <p className="py-8 text-center text-sm text-foreground/80">No mapped routes for this day yet.</p>
            ) : (
                <div className="flex gap-3 overflow-x-auto pb-1">
                    {visibleRoutes.map((route) => {
                        const tech = techById.get(route.techId);
                        if (!tech) return null;
                        return (
                            <RouteCard
                                key={tech.id}
                                tech={tech}
                                route={route}
                                shop={shop}
                                color={stableRouteColor(route.techId, routes)}
                                suggestion={suggestionsByTech?.get(tech.id) ?? null}
                                onExpand={onExpandTech ? () => onExpandTech(tech.id) : undefined}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/**
 * Stable per-tech route colour. Used by both `RouteRail` (dispatch shell)
 * and `DispatchMapPage` so a tech's polyline keeps the same colour
 * across both surfaces — see PRE-CUTOVER-UX-FEEDBACK #42 for context.
 *
 * The colour is picked from `ROUTE_HEX` using the tech's index inside
 * the `routes` array returned by `/api/dispatch/tech-routes`. That
 * array's order is `j.assigned_to ASC` on the server, so it's stable
 * across calls for the same day + the same set of assigned techs.
 *
 * If the tech somehow isn't in the routes list (e.g. the caller passes
 * `[]` while loading), we fall back to the first colour so callers
 * never receive `undefined`.
 */
export function stableRouteColor(techId: string, routes: TechRoute[]): string {
    const idx = routes.findIndex((r) => r.techId === techId);
    if (idx < 0) return ROUTE_HEX[0] ?? "#3b82f6";
    return ROUTE_HEX[idx % ROUTE_HEX.length] ?? "#3b82f6";
}

function RailHeader() {
    return (
        <div className="mb-2 flex items-center gap-2">
            <h3 className="text-sm font-semibold">Routes by technician</h3>
            {/* #828 M2: "today's" lied on any other selected date. */}
            <span className="text-[11px] text-foreground/80">
                Each card is a mini map of the selected day&apos;s planned stops.
            </span>
        </div>
    );
}

interface RouteCardProps {
    tech: DispatchTech;
    route: TechRoute;
    shop: ShopLocation | null;
    color: string;
    suggestion: { jobCount: number; label: string } | null;
    onExpand?: () => void;
}

function RouteCard({ tech, route, shop, color, suggestion, onExpand }: RouteCardProps) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<L.Map | null>(null);
    const layerRef = useRef<L.LayerGroup | null>(null);

    useEffect(() => {
        if (!containerRef.current || mapRef.current) return;
        const map = L.map(containerRef.current, {
            zoomControl: false,
            attributionControl: false,
            dragging: false,
            scrollWheelZoom: false,
            doubleClickZoom: false,
            boxZoom: false,
            keyboard: false,
            touchZoom: false,
        }).setView([32.85, -97.05], 10);
        addBasemapLayer(L, map, "street");
        layerRef.current = L.layerGroup().addTo(map);
        mapRef.current = map;
        return () => {
            map.remove();
            mapRef.current = null;
            layerRef.current = null;
        };
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        const layer = layerRef.current;
        if (!map || !layer) return;
        layer.clearLayers();

        const stops = route.jobs.filter(
            (s): s is typeof s & { latitude: number; longitude: number } =>
                Number.isFinite(s.latitude) && Number.isFinite(s.longitude),
        );
        if (stops.length === 0) return;

        const hasShop = shop && Number.isFinite(shop.lat) && Number.isFinite(shop.lng);
        const shopCoord: L.LatLngExpression | null = hasShop ? [shop.lat, shop.lng] : null;

        const stopCoords: L.LatLngExpression[] = stops.map((s) => [s.latitude, s.longitude]);
        const lineCoords: L.LatLngExpression[] = [
            ...(shopCoord ? [shopCoord] : []),
            ...stopCoords,
            ...(shopCoord ? [shopCoord] : []),
        ];

        if (lineCoords.length > 1) {
            L.polyline(lineCoords, {
                color,
                weight: 4,
                opacity: 1.0,
            }).addTo(layer);
        }

        if (shopCoord) {
            L.marker(shopCoord, { icon: shopIcon(), zIndexOffset: 1000 })
                .bindTooltip("Shop", { permanent: true, direction: "top", offset: [0, -12], className: "shop-label" })
                .addTo(layer);
        }

        stops.forEach((stop, i) => {
            L.marker([stop.latitude, stop.longitude], {
                icon: numberedIcon(i + 1, color),
            })
                .bindTooltip(`${i + 1}. ${stop.title}`, { direction: "top", offset: [0, -8] })
                .addTo(layer);
        });

        const allCoords = [...stopCoords, ...(shopCoord ? [shopCoord] : [])];
        const bounds = L.latLngBounds(allCoords);
        // animate:false skips Leaflet's zoom-transition setTimeout, which can
        // otherwise fire after this card unmounts and crash inside
        // _onZoomTransitionEnd on an already-removed map pane (_leaflet_pos).
        map.fitBounds(bounds, { padding: [12, 12], maxZoom: 13, animate: false });
        const resizeTimer = window.setTimeout(() => {
            if (mapRef.current) mapRef.current.invalidateSize();
        }, 50);
        return () => window.clearTimeout(resizeTimer);
    }, [route, shop, color]);

    return (
        <div className="group w-80 shrink-0 overflow-hidden rounded-lg border bg-background">
            <div className="flex items-center justify-between gap-2 border-b px-2.5 py-1.5">
                <div className="flex items-center gap-2 truncate">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                    <span className="truncate text-xs font-semibold">
                        {tech.first_name} {tech.last_name}
                    </span>
                </div>
                <Badge variant="secondary" className="text-[10px]">
                    {route.jobs.length} stop{route.jobs.length === 1 ? "" : "s"}
                </Badge>
            </div>
            <div className="relative">
                <div
                    ref={containerRef}
                    className={`h-48 w-full bg-muted/40 ${onExpand ? "cursor-pointer" : ""}`}
                    onClick={onExpand}
                    role={onExpand ? "button" : undefined}
                    title={onExpand ? "Open expanded route" : undefined}
                />
                {onExpand && (
                    <button
                        type="button"
                        onClick={onExpand}
                        title="Open expanded route"
                        className="absolute right-1.5 top-1.5 z-[400] rounded-md border bg-background/95 p-1 text-foreground/80 shadow-sm opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100 focus:opacity-100"
                    >
                        <Maximize2 className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>
            {suggestion && (
                <div className="flex items-center gap-1.5 border-t bg-amber-50 px-2.5 py-1 text-[10px] font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
                    <Sparkles className="h-3 w-3 shrink-0" />
                    <span className="truncate">
                        {suggestion.jobCount} nearby unscheduled in {suggestion.label}
                    </span>
                </div>
            )}
            <RouteCardOrderList stops={route.jobs} shopLabel={shop?.label ?? null} />
        </div>
    );
}

function RouteCardOrderList({ stops, shopLabel }: { stops: TechRouteStop[]; shopLabel: string | null }) {
    if (stops.length === 0) return null;
    const shopRow = shopLabel ? (
        <li className="flex items-center gap-1.5 truncate text-foreground/80">
            <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded bg-orange-700 text-white text-[9px] font-bold">
                ⌂
            </span>
            <span className="truncate italic font-medium">Shop</span>
        </li>
    ) : null;
    const unmappedCount = stops.filter((s) => s.unmapped).length;
    return (
        <>
            <ol className="max-h-28 space-y-0.5 overflow-y-auto px-2.5 py-1.5 text-[11px]">
                {shopRow}
                {stops.map((s, i) => (
                    <li
                        key={s.jobId}
                        className="flex items-center gap-1.5 truncate"
                        title={s.unmapped ? "No site coordinates — not shown on map" : undefined}
                    >
                        <span
                            className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold ${
                                s.unmapped
                                    ? "bg-amber-200 text-amber-900 dark:bg-amber-700/60 dark:text-amber-100"
                                    : "bg-muted"
                            }`}
                        >
                            {s.unmapped ? "?" : i + 1}
                        </span>
                        <span className="truncate text-foreground">{s.title}</span>
                        <span className="ml-auto truncate text-foreground/80">{s.site_name}</span>
                    </li>
                ))}
                {shopRow}
            </ol>
            {unmappedCount > 0 && (
                <div className="border-t bg-amber-50 px-2.5 py-1 text-[10px] font-medium text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">
                    {unmappedCount} stop{unmappedCount === 1 ? "" : "s"} without coordinates — not on map
                </div>
            )}
        </>
    );
}

function shopIcon(): L.DivIcon {
    return L.divIcon({
        className: "dispatch-rail-shop-pin",
        html: `<div style="
            background-color: #c2410c;
            width: 32px;
            height: 32px;
            border-radius: 6px;
            border: 3px solid white;
            box-shadow: 0 0 0 3px rgba(194,65,12,0.5), 0 2px 8px rgba(0,0,0,0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: 700;
            font-size: 17px;
            font-family: system-ui, sans-serif;
            position: relative;
            z-index: 10000;
        ">⌂</div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
    });
}

function numberedIcon(num: number, color: string): L.DivIcon {
    return L.divIcon({
        className: "dispatch-rail-pin",
        html: `<div style="
            background-color: ${color};
            width: 22px;
            height: 22px;
            border-radius: 50%;
            border: 2px solid white;
            box-shadow: 0 1px 3px rgba(0,0,0,0.35);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: 700;
            font-size: 11px;
            font-family: system-ui, sans-serif;
        ">${num}</div>`,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
    });
}

/** Inject the leaflet stylesheet once on first mount. */
function useLeafletCss() {
    useEffect(() => {
        const linkId = "leaflet-css-link";
        if (document.getElementById(linkId)) return;
        const link = document.createElement("link");
        link.id = linkId;
        link.rel = "stylesheet";
        link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
        link.integrity = "sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=";
        link.crossOrigin = "";
        document.head.appendChild(link);
    }, []);
}
